<template>
  <div class="p-4">
    <h4 class="mb-3">Template wählen</h4>
    <p class="text-muted mb-4">Wähle eine Template für deine Quittung aus.</p>

    <!-- Template Loading State -->
    <div v-if="templatesLoading" class="text-center py-5">
      <div class="loading-animation">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <h5 class="mt-3 mb-2">Templates werden geladen</h5>
        <p class="text-muted mb-0">Bitte warte einen Moment...</p>
      </div>
    </div>

    <!-- Template Selection -->
    <div v-else class="row g-3 mb-3">
      <div
        v-for="template in availableTemplates"
        :key="template.id"
        class="col-md-6 col-lg-4"
      >
        <div
          class="template-card"
          :class="{
            selected: selectedTemplateId === template.id,
            'pro-feature': isProFeature(template),
          }"
          @click="selectTemplate(template)"
        >
          <div class="template-card-header">
            <h6 class="mb-1">
              {{ template.customName || template.template?.displayName }}
            </h6>
            <span
              v-if="template.template?.isProFeature || template.isProFeature"
              class="badge bg-primary"
            >
              Pro
            </span>
            <span v-if="template.isDefault" class="badge bg-success"> Standard </span>
          </div>
          <p class="template-description">
            {{ template.template?.description || template.description }}
          </p>
          <div class="template-fields">
            <small class="text-muted">
              {{ template.template?.fields?.length || template.fields?.length || 0 }} Felder
            </small>
            <div class="template-categories">
              <span
                v-for="category in getTemplateCategories(template)"
                :key="category"
                class="badge bg-secondary me-1"
              >
                {{ getCategoryLabel(category, template.template || template) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pro Feature Warning -->
    <div
      v-if="showProFeatureWarning"
      class="alert alert-gradient-warning mt-3"
    >
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Dieses Template ist nur für Pro-Teams verfügbar. Upgrade dein Team, um diese Template zu
      verwenden.
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { ReceiptWizardSymbol } from '../../tokens'

const wizard = inject(ReceiptWizardSymbol)

if (!wizard) {
  throw new Error('StepTemplateSelection requires ReceiptWizard context')
}

const templatesLoading = wizard.templatesLoading
const availableTemplates = wizard.availableTemplates
const formData = wizard.formData
const selectedTemplateId = computed(() => formData.value.teamTemplateId)
const selectedTemplate = wizard.selectedTemplate
const selectedTeam = wizard.selectedTeam
const getTemplateCategories = wizard.getTemplateCategories
const getCategoryLabel = wizard.getCategoryLabel
const selectTemplate = wizard.selectTemplate
const autoTemplateSelectionInProgress = wizard.autoTemplateSelectionInProgress
const isProFeature = (template) => {
  const actualTemplate = template.template || template
  const requiresPro = actualTemplate?.isProFeature || template.isProFeature
  if (!requiresPro) {
    return false
  }
  const team = selectedTeam.value
  if (!team) {
    return false
  }
  return !(team.status === 'pro' || team.status === 'ultimate')
}
const attemptedDefaultSelection = ref(false)

watch(
  () => availableTemplates.value,
  () => {
    attemptedDefaultSelection.value = false
  },
)

watch(
  () => ({
    templates: availableTemplates.value,
    currentSelection: selectedTemplate.value,
  }),
  ({ templates, currentSelection }) => {
    if (
      attemptedDefaultSelection.value ||
      currentSelection ||
      !templates ||
      templates.length === 0
    ) {
      return
    }

    const defaultTemplate = templates.find((tpl) => tpl.isDefault)
    if (!defaultTemplate) {
      attemptedDefaultSelection.value = true
      return
    }

    if (isProFeature(defaultTemplate)) {
      attemptedDefaultSelection.value = true
      return
    }

    attemptedDefaultSelection.value = true
    autoTemplateSelectionInProgress.value = true
    selectTemplate(defaultTemplate)
  },
  { immediate: true, deep: false },
)

const showProFeatureWarning = computed(() => {
  const team = selectedTeam.value
  const tpl = selectedTemplate.value
  if (!team || !tpl) {
    return false
  }
  const requiresPro = tpl.template?.isProFeature || tpl.isProFeature
  if (!requiresPro) {
    return false
  }
  return !(team.status === 'pro' || team.status === 'ultimate')
})
</script>
